import { Cable } from 'lucide-react';
import type { ReactNode } from 'react';

// Front views of the connectors, kept simple enough to read at 24px.
const CONNECTOR_GRAPHICS: Readonly<Record<string, ReactNode>> = {
  Composite: (
    <>
      <circle cx="16" cy="16" r="11" fill="#d6dde0" />
      <circle cx="16" cy="16" r="8" fill="#f0c645" stroke="#98741e" strokeWidth="1.5" />
      <circle cx="16" cy="16" r="3.5" fill="#26343c" stroke="#fff0ae" strokeWidth="1.5" />
    </>
  ),
  'S-Video': (
    <>
      <circle cx="16" cy="16" r="11" fill="#d6dde0" />
      <circle cx="16" cy="16" r="8.5" fill="#26343c" stroke="none" />
      <g fill="#d6dde0" stroke="none">
        <circle cx="12.5" cy="12" r="1.5" />
        <circle cx="19.5" cy="12" r="1.5" />
        <circle cx="11" cy="17" r="1.5" />
        <circle cx="21" cy="17" r="1.5" />
        <rect x="13.5" y="19.5" width="5" height="3" rx="0.7" />
      </g>
    </>
  ),
  'Component (YPbPr)': (
    <>
      <rect x="1" y="9" width="30" height="14" rx="4" fill="#d6dde0" strokeWidth="1.3" />
      <circle cx="6" cy="16" r="4" fill="#58af79" stroke="#296344" strokeWidth="1.2" />
      <circle cx="16" cy="16" r="4" fill="#659edc" stroke="#345b88" strokeWidth="1.2" />
      <circle cx="26" cy="16" r="4" fill="#df7267" stroke="#98453f" strokeWidth="1.2" />
      <g fill="#26343c" stroke="none">
        <circle cx="6" cy="16" r="1.5" />
        <circle cx="16" cy="16" r="1.5" />
        <circle cx="26" cy="16" r="1.5" />
      </g>
    </>
  ),
  RGBS: (
    <>
      <path d="M3 8.5h22l4 4V23H3Z" fill="#d6dde0" />
      <path d="M5.5 11h18.5l2.5 2.5v7H5.5Z" fill="#26343c" stroke="none" />
      <path d="M8 13v1.5m2.3-1.5v1.5m2.3-1.5v1.5m2.3-1.5v1.5m2.3-1.5v1.5m2.3-1.5v1.5m2.3-1.5v1.5m2.3-1.5v1.5M8 17v1.5m2.3-1.5v1.5m2.3-1.5v1.5m2.3-1.5v1.5m2.3-1.5v1.5m2.3-1.5v1.5m2.3-1.5v1.5m2.3-1.5v1.5" stroke="#d6dde0" strokeWidth="1" />
    </>
  ),
  RGBHV: (
    <>
      <rect x="1" y="9" width="30" height="14" rx="3" fill="#d6dde0" strokeWidth="1.3" />
      <path d="M7 10.5h18l-2 11H9Z" fill="#5987be" stroke="#345b88" strokeWidth="1.2" />
      <g fill="#26343c" stroke="none">
        <circle cx="4" cy="16" r="1.2" />
        <circle cx="28" cy="16" r="1.2" />
        {[13, 16, 19].flatMap((y) => [10.5, 13.2, 15.9, 18.6, 21.3].map((x) => (
          <circle key={`${x}-${y}`} cx={x + (y === 16 ? 0.5 : 0)} cy={y} r="0.8" />
        )))}
      </g>
    </>
  ),
  HDMI: (
    <>
      <path d="M3 9.5h26v7l-4.5 6h-17L3 16.5Z" fill="#d6dde0" />
      <path d="M6 12h20v3.5l-3.5 4.5h-13L6 15.5Z" fill="#26343c" stroke="none" />
      <path d="M9 15h14" stroke="#e3bd68" strokeWidth="2.5" strokeLinecap="butt" />
      <path d="M11 14v2m2.5-2v2m2.5-2v2m2.5-2v2m2.5-2v2" stroke="#26343c" strokeWidth="0.8" />
    </>
  ),
};

export function ConnectionIcon({ connection }: { connection: string }) {
  const graphic = CONNECTOR_GRAPHICS[connection];
  if (!graphic) return <Cable className="connection-icon" size={22} aria-hidden="true" />;

  return (
    <svg className="connection-icon" width="24" height="24" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round" aria-hidden="true" focusable="false">
      {graphic}
    </svg>
  );
}
